"use client";
import {OneToastContainer, toast} from "@/components/commons/one-toast-container";
import {Button} from "@/components/ui/button";
import {Form, FormControl, FormField, FormItem, FormLabel, FormMessage} from "@/components/ui/form";
import {Input} from "@/components/ui/input";
import {cn} from "@/lib/utils";
import {zodResolver} from "@hookform/resolvers/zod";
import Link from "next/link";
import {useRouter} from "next/navigation";
import {useState} from "react";
import {useForm} from "react-hook-form";
import {z} from "zod";
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from "@/components/ui/card";

const signupSchema = z.object({
  name: z.string().min(2, {message: "Name is required"}),
  email: z.string().email({message: "Invalid email address"}),
  password: z.string().min(6, {message: "Password must be at least 6 characters"}),
  otp: z.string().optional(),
});

export function SignupForm({className, ...props}: React.ComponentProps<"form">) {
  const [showOtp, setShowOtp] = useState(false);
  const [emailForOtp, setEmailForOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const router = useRouter();

  const form = useForm<z.infer<typeof signupSchema>>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      name: "",
      email: "",
      password: "",
      otp: "",
    },
  });

  const onSubmit = async (values: z.infer<typeof signupSchema>) => {
    setLoading(true);
    if (!showOtp) {
      // Signup step
      try {
        const res = await fetch("/api/users/signup", {
          method: "POST",
          headers: {"Content-Type": "application/json"},
          body: JSON.stringify({
            name: values.name,
            email: values.email,
            password: values.password,
          }),
        });
        const data = await res.json();
        if (res.ok && data.status === "success") {
          setShowOtp(true);
          setEmailForOtp(values.email);
          toast("Please check your email for the OTP.");
        } else {
          toast(data?.message || "Signup failed");
        }
      } catch (err) {
        toast("Network error. Please try again.");
      } finally {
        setLoading(false);
      }
    } else {
      // OTP step
      try {
        const res = await fetch("/api/users/verify-otp", {
          method: "POST",
          headers: {"Content-Type": "application/json"},
          body: JSON.stringify({
            email: emailForOtp,
            otp: values.otp,
          }),
        });
        const data = await res.json();
        if (res.ok && data.status === "success") {
          toast("Account confirmed! Redirecting to login page");
          setTimeout(() => {
            router.push("/login");
          }, 2000);
        } else {
          toast.error(data?.error || data?.message || "OTP verification failed");
        }
      } catch (err) {
        console.error(err);
        toast.error("Network error. Please try again.");
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className={cn("flex flex-col gap-6", className)}>
      <OneToastContainer />
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-xl">Create your account</CardTitle>
          <CardDescription>Sign up to get started with rtZen</CardDescription>
        </CardHeader>
        <CardContent className="pt-6 pl-6 pr-6 pb-6 w-full">
          <Form {...form}>
            <form className={cn("flex flex-col gap-6", className)} onSubmit={form.handleSubmit(onSubmit)} {...props}>
              <div className="grid gap-6">
                {!showOtp && (
                  <>
                    <FormField
                      control={form.control}
                      name="name"
                      render={({field}) => (
                        <FormItem>
                          <FormLabel>Name</FormLabel>
                          <FormControl>
                            <Input placeholder="Your Name" {...field} disabled={loading} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="email"
                      render={({field}) => (
                        <FormItem>
                          <FormLabel>Email</FormLabel>
                          <FormControl>
                            <Input placeholder="me@mycompany.com" type="email" {...field} disabled={loading} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="password"
                      render={({field}) => (
                        <FormItem>
                          <FormLabel>Password</FormLabel>
                          <FormControl>
                            <Input type="password" {...field} disabled={loading} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </>
                )}
                {showOtp && (
                  <FormField
                    control={form.control}
                    name="otp"
                    render={({field}) => (
                      <FormItem>
                        <FormLabel>OTP</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter OTP" {...field} disabled={loading} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
                <Button type="submit" className="w-full" disabled={loading}>
                  {showOtp ? "Confirm" : "Sign Up"}
                </Button>
              </div>
              <div className="text-center text-sm">
                Already have an account?{" "}
                <Link href="/login" className="underline underline-offset-4">
                  Login
                </Link>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
